import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

/** The user's stored preference. `system` follows the OS setting. */
export type ThemeMode = 'light' | 'dark' | 'system'

/** The mode actually applied to the UI once `system` is resolved. */
type EffectiveThemeMode = 'light' | 'dark'

// Kept in sync with the inline pre-paint script in index.html.
const STORAGE_KEY = 'caya-mailroom-theme-mode'

const DARK_QUERY = '(prefers-color-scheme: dark)'

type ThemeModeContextValue = {
  effectiveMode: EffectiveThemeMode
  mode: ThemeMode
  setMode: (mode: ThemeMode) => void
}

const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(
  undefined
)

const isThemeMode = (value: string | null): value is ThemeMode =>
  value === 'light' || value === 'dark' || value === 'system'

const readStoredMode = (): ThemeMode => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return isThemeMode(stored) ? stored : 'light'
  } catch {
    return 'light'
  }
}

/**
 * Owns the mailroom dark-mode preference: persists it, resolves `system`
 * against `prefers-color-scheme`, and reflects the result onto the
 * `<html data-theme>` attribute that the CSS and DesignContainer key off. Must
 * wrap DesignContainer so the antd algorithm can read the resolved mode.
 */
export const ThemeModeProvider = ({ children }: PropsWithChildren) => {
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode)
  const [systemPrefersDark, setSystemPrefersDark] = useState(
    () => window.matchMedia(DARK_QUERY).matches
  )

  useEffect(() => {
    const query = window.matchMedia(DARK_QUERY)
    const onChange = (event: MediaQueryListEvent) =>
      setSystemPrefersDark(event.matches)
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])

  const effectiveMode: EffectiveThemeMode =
    mode === 'system' ? (systemPrefersDark ? 'dark' : 'light') : mode

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', effectiveMode)
  }, [effectiveMode])

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Ignore storage failures (private mode); the in-memory choice still applies.
    }
  }, [])

  const value = useMemo(
    () => ({ effectiveMode, mode, setMode }),
    [effectiveMode, mode, setMode]
  )

  return (
    <ThemeModeContext.Provider value={value}>
      {children}
    </ThemeModeContext.Provider>
  )
}

export const useThemeMode = (): ThemeModeContextValue => {
  const context = useContext(ThemeModeContext)
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider')
  }
  return context
}
